import React from 'react';
import {
    Box,
    Card,
    CardBody,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    Badge,
    Flex,
    HStack,
    IconButton,
    Select,
    Button,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { WeighbridgeRow, PaginationMeta } from '../types';
import { ALL_COLUMNS } from './columnsConfig';

interface WeighbridgeTableProps {
    records: WeighbridgeRow[];
    visibleColumns: string[];
    pagination: PaginationMeta;
    onChangePage: (page: number) => void;
    onChangePerPage: (perPage: number) => void;
    loading?: boolean;
    error?: string | null;
}

export const WeighbridgeTable: React.FC<WeighbridgeTableProps> = ({
    records,
    visibleColumns,
    pagination,
    onChangePage,
    onChangePerPage,
    loading = false,
    error = null,
}) => {
    const activeCols = ALL_COLUMNS.filter((col) => visibleColumns.includes(col.key as string));

    if (loading) {
        return (
            <Card bg="white" shadow="sm" borderRadius="lg">
                <CardBody p={12}>
                    <Center flexDirection="column" gap={3}>
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                        <Text color="gray.500" fontSize="sm">
                            Memuat data...
                        </Text>
                    </Center>
                </CardBody>
            </Card>
        );
    }

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
            </Alert>
        );
    }

    if (!records || records.length === 0) {
        return (
            <Card bg="white" shadow="sm" borderRadius="lg">
                <CardBody p={8}>
                    <Center flexDirection="column">
                        <Text color="gray.500" fontSize="sm">
                            Tidak ada data yang ditemukan.
                        </Text>
                    </Center>
                </CardBody>
            </Card>
        );
    }

    const formatCell = (row: WeighbridgeRow, colKey: keyof WeighbridgeRow, isNumeric?: boolean) => {
        const val = row[colKey];
        if (val === null || val === undefined) return '-';

        if (typeof val === 'boolean') {
            return val ? (
                <Badge colorScheme="red" fontSize="2xs">Ya</Badge>
            ) : (
                <Badge colorScheme="gray" fontSize="2xs">Tidak</Badge>
            );
        }

        if (isNumeric && typeof val === 'number') {
            return val.toLocaleString('id-ID', { maximumFractionDigits: 2 });
        }

        // Hanya format jika string merupakan format tanggal ISO (misal: 2026-10-20T12:00:00)
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
            return val.split('T')[0];
        }

        return String(val);
    };

    const { page, per_page, total_records, total_pages } = pagination;
    const startRecord = (page - 1) * per_page + 1;
    const endRecord = Math.min(page * per_page, total_records);

    // Hitung nomor halaman navigasi (maks 5 tombol nomor)
    const getPageNumbers = () => {
        const pages: number[] = [];
        const start = Math.max(1, page - 2);
        const end = Math.min(total_pages, page + 2);
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <Card bg="white" shadow="sm" borderRadius="lg" overflow="hidden">
            <CardBody p={0}>
                <Box maxH="650px" overflowY="auto" overflowX="auto">
                    <Table size="sm" variant="simple">
                        <Thead bg="gray.100" position="sticky" top={0} zIndex={10}>
                            <Tr>
                                <Th width="55px" textAlign="center" fontSize="2xs" color="gray.600">
                                    No
                                </Th>
                                {activeCols.map((col) => (
                                    <Th
                                        key={col.key as string}
                                        fontSize="2xs"
                                        color="gray.600"
                                        textAlign={col.isNumeric ? 'right' : 'left'}
                                        whiteSpace="nowrap"
                                        minWidth={col.key === 'company' ? '220px' : undefined}
                                        px={3}
                                        py={3}
                                    >
                                        {col.label}
                                    </Th>
                                ))}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {records.map((row, idx) => (
                                <Tr
                                    key={`${row.batch_line_id || idx}-${row.wb_ticket_no || ''}`}
                                    _hover={{ bg: 'blue.50' }}
                                    transition="background-color 0.15s"
                                >
                                    <Td textAlign="center" fontSize="xs" color="gray.500">
                                        {startRecord + idx}
                                    </Td>
                                    {activeCols.map((col) => (
                                        <Td
                                            key={col.key as string}
                                            fontSize="xs"
                                            textAlign={col.isNumeric ? 'right' : 'left'}
                                            whiteSpace="nowrap"
                                            minWidth={col.key === 'company' ? '220px' : undefined}
                                            px={3}
                                            py={2}
                                        >
                                            {formatCell(row, col.key, col.isNumeric)}
                                        </Td>
                                    ))}
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>

                {/* Pagination Controls */}
                <Flex
                    direction={{ base: 'column', md: 'row' }}
                    justify="space-between"
                    align="center"
                    px={4}
                    py={3}
                    borderTop="1px solid"
                    borderColor="gray.200"
                    gap={3}
                >
                    <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.600">
                            Baris per halaman:
                        </Text>
                        <Select
                            size="xs"
                            borderRadius="md"
                            width="75px"
                            value={per_page}
                            onChange={(e) => onChangePerPage(Number(e.target.value))}
                        >
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={250}>250</option>
                        </Select>
                        <Text fontSize="xs" color="gray.500">
                            Menampilkan {startRecord.toLocaleString('id-ID')} - {endRecord.toLocaleString('id-ID')} dari {total_records.toLocaleString('id-ID')} data
                        </Text>
                    </HStack>

                    <HStack spacing={1}>
                        <IconButton
                            aria-label="First page"
                            size="xs"
                            icon={<Text fontSize="xs">{'«'}</Text>}
                            isDisabled={page <= 1}
                            onClick={() => onChangePage(1)}
                        />
                        <IconButton
                            aria-label="Previous page"
                            size="xs"
                            icon={<ChevronLeftIcon />}
                            isDisabled={page <= 1}
                            onClick={() => onChangePage(page - 1)}
                        />

                        {getPageNumbers().map((p) => (
                            <Button
                                key={p}
                                size="xs"
                                colorScheme={p === page ? 'blue' : 'gray'}
                                variant={p === page ? 'solid' : 'ghost'}
                                onClick={() => onChangePage(p)}
                            >
                                {p}
                            </Button>
                        ))}

                        <IconButton
                            aria-label="Next page"
                            size="xs"
                            icon={<ChevronRightIcon />}
                            isDisabled={page >= total_pages}
                            onClick={() => onChangePage(page + 1)}
                        />
                        <IconButton
                            aria-label="Last page"
                            size="xs"
                            icon={<Text fontSize="xs">{'»'}</Text>}
                            isDisabled={page >= total_pages}
                            onClick={() => onChangePage(total_pages)}
                        />
                    </HStack>
                </Flex>
            </CardBody>
        </Card>
    );
};
